import { useEffect, useMemo, useState } from "react";
import {
  FiDownload,
  FiEdit2,
  FiEye,
  FiMail,
  FiPhone,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUsers,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import CustomerModal from "../components/CustomerModal";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export default function CustomersPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      setError("");

      const params = new URLSearchParams();

      if (search.trim()) params.set("search", search.trim());
      if (status !== "All") params.set("status", status);

      const response = await apiRequest(`/customers?${params.toString()}`, {
        accessToken,
      });

      setCustomers(response);
    } catch (requestError) {
      setError(requestError.message || "Unable to load customers.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (accessToken) loadCustomers();
    }, 300);

    return () => clearTimeout(timer);
  }, [search, status, accessToken]);

  const metrics = useMemo(() => {
    const thisMonthStart = new Date();
    thisMonthStart.setDate(1);
    thisMonthStart.setHours(0, 0, 0, 0);

    return {
      total: customers.length,
      active: customers.filter((customer) => customer.status === "ACTIVE")
        .length,
      newThisMonth: customers.filter(
        (customer) => new Date(customer.createdAt) >= thisMonthStart,
      ).length,
      loyaltyPoints: customers.reduce(
        (total, customer) =>
          total + Number(customer.loyaltyAccount?.pointsBalance || 0),
        0,
      ),
    };
  }, [customers]);

  const exportCustomers = () => {
    const rows = [
      ["Customer", "Phone", "Email", "City", "Orders", "Loyalty Points", "Status"],
      ...customers.map((customer) => [
        customer.fullName,
        customer.phone || "",
        customer.email || "",
        customer.addresses?.[0]?.city || "",
        totalOrders(customer),
        Number(customer.loyaltyAccount?.pointsBalance || 0),
        customer.status,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","),
      )
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    link.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const deleteCustomer = async (customer) => {
    const confirmed = window.confirm(
      `Disable and remove ${customer.fullName} from active customer lists?`,
    );

    if (!confirmed) return;

    try {
      await apiRequest(`/customers/${customer.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadCustomers();
    } catch (requestError) {
      setError(requestError.message || "Unable to remove customer.");
    }
  };

  const openCreate = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <p className="text-sm text-gray-500">Sales / Customers</p>

          <h1 className="text-3xl font-bold mt-1">Customer Management</h1>

          <p className="text-gray-500 mt-2">
            Manage customer profiles, loyalty balances, and purchase history.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            disabled
            title="Bulk customer import will be added later"
            className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 opacity-50 cursor-not-allowed"
          >
            <FiUpload />
            Import
          </button>

          <button
            onClick={exportCustomers}
            disabled={!customers.length}
            className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 disabled:opacity-50"
          >
            <FiDownload />
            Export
          </button>

          <button
            onClick={openCreate}
            className="h-11 px-5 rounded-xl bg-primary-700 text-white flex items-center gap-2"
          >
            <FiPlus />
            Add Customer
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-5 mb-8">
        <MetricCard title="Total Customers" value={metrics.total} />

        <MetricCard
          title="Active Customers"
          value={metrics.active}
          valueClass="text-green-600"
        />

        <MetricCard
          title="New This Month"
          value={metrics.newThisMonth}
          valueClass="text-blue-600"
        />

        <MetricCard
          title="Loyalty Points Issued"
          value={metrics.loyaltyPoints.toLocaleString()}
          valueClass="text-orange-600"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="relative w-full lg:w-96">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name, phone, or email..."
              className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <div className="flex gap-3">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-4 bg-white"
            >
              <option value="All">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="DISABLED">Disabled</option>
            </select>

            <button
              onClick={loadCustomers}
              className="h-12 px-4 rounded-xl border border-gray-200 flex items-center gap-2"
            >
              <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <Header>Customer</Header>
                <Header>Contact</Header>
                <Header>Location</Header>
                <Header align="center">Orders</Header>
                <Header align="center">Loyalty Points</Header>
                <Header align="center">Status</Header>
                <Header align="right">Actions</Header>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="py-16 text-center text-gray-500">
                    Loading customers...
                  </td>
                </tr>
              ) : customers.length ? (
                customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-700">
                          <FiUsers size={20} />
                        </div>

                        <div>
                          <h4 className="font-semibold">{customer.fullName}</h4>

                          <p className="text-xs text-gray-500">
                            ID: {customer.id.slice(0, 8).toUpperCase()}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <FiPhone size={13} />
                          {customer.phone || "—"}
                        </div>

                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <FiMail size={13} />
                          {customer.email || "—"}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <p>{customer.addresses?.[0]?.city || "—"}</p>

                      <p className="text-xs text-gray-500 mt-1">
                        {customer.addresses?.[0]?.state || "No address"}
                      </p>
                    </td>

                    <td className="px-6 py-4 text-center font-semibold">
                      {totalOrders(customer)}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-primary-700">
                        {Number(
                          customer.loyaltyAccount?.pointsBalance || 0,
                        ).toLocaleString()}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <StatusBadge status={customer.status} />
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() =>
                            navigate(`/admindashboard/sales/customers/${customer.id}`)
                          }
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white transition"
                          title="View customer"
                        >
                          <FiEye />
                        </button>

                        <button
                          onClick={() => openEdit(customer)}
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-blue-600 hover:text-white transition"
                          title="Edit customer"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          onClick={() => deleteCustomer(customer)}
                          className="h-9 w-9 rounded-lg border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition"
                          title="Disable customer"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-16 text-center text-gray-500">
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-4 p-5 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Showing <strong>{customers.length}</strong> customer
            {customers.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <CustomerModal
        open={showModal}
        onClose={() => setShowModal(false)}
        customer={editingCustomer}
        onSaved={loadCustomers}
      />
    </div>
  );
}

function totalOrders(customer) {
  return (
    Number(customer._count?.ecommerceOrders || 0) +
    Number(customer._count?.posSales || 0) +
    Number(customer._count?.salesOrders || 0)
  );
}

function Header({ children, align = "left" }) {
  return (
    <th
      className={`px-6 py-4 text-${align} text-xs font-semibold uppercase text-gray-500`}
    >
      {children}
    </th>
  );
}

function MetricCard({ title, value, valueClass = "" }) {
  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6">
      <p className="text-gray-500 text-sm">{title}</p>
      <h2 className={`text-3xl font-bold mt-2 ${valueClass}`}>{value}</h2>
    </div>
  );
}

function StatusBadge({ status }) {
  const active = status === "ACTIVE";

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        active
          ? "bg-green-100 text-green-700"
          : "bg-red-100 text-red-700"
      }`}
    >
      {active ? "Active" : "Disabled"}
    </span>
  );
}